import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  Button,
  DropZone,
  LegacyStack,
  Thumbnail,
  ProgressBar,
  Banner,
} from '@shopify/polaris';
import { DeleteMinor } from '@shopify/polaris-icons';
import { authService } from '../../services/authService';
import { getServerURL } from '../../services/config';
import { ordersService } from '../../services/ordersService';

const SERVER_URL = getServerURL();

const AddKidPhotosModal = ({
  isOpen,
  onClose,
  orderId,
  lineItemId,
  currentPhotos = [],
  setToastMessage,
  setToastActive,
  onPhotosAdded
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [fileErrors, setFileErrors] = useState(new Map());
  const [validationError, setValidationError] = useState('');
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [deletingPhoto, setDeletingPhoto] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setExistingPhotos(currentPhotos);
    } else {
      setSelectedFiles([]);
      setFileErrors(new Map());
      setValidationError('');
      setProcessingProgress(0);
      setExistingPhotos([]);
      setDeletingPhoto(null);
    }
  }, [currentPhotos, isOpen]);

  // Convert file to base64
  const fileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Upload file using backend endpoint
  const uploadFileViaBackend = useCallback(async (file) => {
    try {
      // Convert file to base64
      const base64Image = await fileToBase64(file);

      // Generate filename
      const ext = file.name.split('.').pop();
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const filename = `order_${orderId}_item_${lineItemId}_${timestamp}_${randomString}.${ext}`;

      // Call backend endpoint
      const response = await fetch(`${SERVER_URL}/upload-kid-image`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          image: base64Image,
          filename: filename
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const data = await response.json();

      if (!data.success || !data.url) {
        throw new Error('Invalid response from upload endpoint');
      }

      return data.url;
    } catch (error) {
      console.error(`Upload failed for ${file.name}:`, error);
      throw error;
    }
  }, [fileToBase64, orderId, lineItemId]);

  // Handle file selection
  const handleDropZoneDrop = useCallback((files) => {
    setSelectedFiles(files);
    setValidationError('');
    setFileErrors(new Map());
  }, []);

  // Remove selected file
  const removeFile = useCallback((index) => {
    setSelectedFiles(files => {
      const newFiles = files.filter((_, i) => i !== index);
      // Remove error for this file
      setFileErrors(prevErrors => {
        const newErrors = new Map(prevErrors);
        newErrors.delete(files[index]?.name);
        return newErrors;
      });
      return newFiles;
    });
  }, []);

  const handleDeleteExistingPhoto = useCallback(async (photo) => {
    const confirmed = window.confirm(
      `Remove ${photo.name || 'this photo'} from this order item?`
    );
    if (!confirmed) return;

    const photoKey = `${photo.name}:${photo.value}`;
    setDeletingPhoto(photoKey);
    try {
      const result = await ordersService.removeLineItemImage(
        orderId,
        lineItemId,
        photo.name,
        photo.value
      );
      setExistingPhotos(photos => photos.filter(existing => (
        existing.name !== photo.name || existing.value !== photo.value
      )));
      setToastMessage('Photo removed successfully');
      setToastActive(true);
      onPhotosAdded?.(result.data);
    } catch (error) {
      setToastMessage(`Failed to remove photo: ${error.message}`);
      setToastActive(true);
    } finally {
      setDeletingPhoto(null);
    }
  }, [
    lineItemId,
    onPhotosAdded,
    orderId,
    setToastActive,
    setToastMessage
  ]);

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (selectedFiles.length === 0) {
      setValidationError('Please select at least one image');
      return;
    }

    setIsUploading(true);
    setProcessingProgress(0);
    const uploadedUrls = [];

    try {
      // Upload files via backend
      const totalFiles = selectedFiles.length;
      for (let i = 0; i < totalFiles; i++) {
        const file = selectedFiles[i];
        try {
          // Update progress for this file
          setProcessingProgress((i / totalFiles) * 90);

          // Upload via backend endpoint
          const url = await uploadFileViaBackend(file);
          uploadedUrls.push(url);

          console.log(`✅ Uploaded ${file.name}: ${url}`);
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          setToastMessage(`Failed to upload ${file.name}: ${error.message}`);
          setToastActive(true);
          // Continue with other files
        }
      }

      if (uploadedUrls.length > 0) {
        // Call backend API to update line item
        setProcessingProgress(95);
        const response = await fetch(`${SERVER_URL}/orders/${orderId}/line-items/${lineItemId}/images`, {
          method: 'PUT',
          headers: authService.getAuthHeaders(),
          body: JSON.stringify({ imageUrls: uploadedUrls }),
        });

        if (response.ok) {
          const data = await response.json();
          setProcessingProgress(100);
          setToastMessage(`Successfully added ${uploadedUrls.length} photo(s)`);
          setToastActive(true);

          // Notify parent component
          if (onPhotosAdded) {
            onPhotosAdded(data.data);
          }

          // Close modal
          onClose();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update line item');
        }
      } else {
        throw new Error('No images were successfully uploaded');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setToastMessage(`Upload failed: ${error.message}`);
      setToastActive(true);
    } finally {
      setIsUploading(false);
      setProcessingProgress(0);
    }
  }, [selectedFiles, uploadFileViaBackend, orderId, lineItemId, setToastMessage, setToastActive, onPhotosAdded, onClose]);

  const fileUpload = !selectedFiles.length && (
    <DropZone.FileUpload actionHint="or drop files to upload" allowMultiple={true} actionTitle='Upload images' />
  );

  const uploadedFilesList = selectedFiles.length > 0 && (
    <LegacyStack vertical>
      {selectedFiles.map((file, index) => {
        const fileError = fileErrors.get(file.name);
        return (
          <LegacyStack alignment="center" key={index}>
            <Thumbnail
              size="small"
              alt={file.name}
              source={URL.createObjectURL(file)}
            />
            <div style={{ flex: 1 }}>
              <Text variant="bodyMd" as="p">
                {file.name}
              </Text>
              <Text variant="bodySm" tone="subdued">{(file.size / 1024 / 1024).toFixed(2)} MB</Text>
              {fileError && (
                <div style={{ marginTop: '4px' }}>
                  <Text variant="bodySm" tone="critical">
                    {fileError}
                  </Text>
                </div>
              )}
            </div>
            <Button
              icon={DeleteMinor}
              onClick={() => removeFile(index)}
              variant="tertiary"
              size="slim"
              disabled={isUploading}
            />
          </LegacyStack>
        );
      })}
    </LegacyStack>
  );
  const isBusy = isUploading || Boolean(deletingPhoto);

  return (
    <Modal
      open={isOpen}
      onClose={isBusy ? () => {} : onClose}
      title={<div style={{padding: '16px'}}>Manage Kid Photos</div>}
      primaryAction={{
        content: isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo(s)`,
        onAction: handleUpload,
        loading: isUploading,
        disabled: selectedFiles.length === 0 || isBusy
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
          disabled: isBusy
        }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {validationError && (
            <Banner status="critical">
              {validationError}
            </Banner>
          )}

          <Text variant="bodyMd">
            View, remove, or upload kid photos for this line item.
          </Text>

          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">Current photos</Text>
            {existingPhotos.length === 0 ? (
              <Text variant="bodySm" tone="subdued">No photos added.</Text>
            ) : (
              <LegacyStack vertical>
                {existingPhotos.map((photo) => {
                  const photoKey = `${photo.name}:${photo.value}`;
                  return (
                    <LegacyStack alignment="center" key={photoKey}>
                      <Thumbnail
                        size="small"
                        alt={photo.name || 'Kid photo'}
                        source={photo.value}
                      />
                      <div style={{ flex: 1 }}>
                        <Text variant="bodyMd" as="p">
                          {photo.name || 'Kid photo'}
                        </Text>
                      </div>
                      <Button
                        icon={DeleteMinor}
                        onClick={() => handleDeleteExistingPhoto(photo)}
                        variant="tertiary"
                        tone="critical"
                        size="slim"
                        loading={deletingPhoto === photoKey}
                        disabled={isUploading || Boolean(deletingPhoto)}
                        accessibilityLabel={`Remove ${photo.name || 'kid photo'}`}
                      />
                    </LegacyStack>
                  );
                })}
              </LegacyStack>
            )}
          </BlockStack>

          <Text variant="headingSm" as="h3">Add photos</Text>
          <DropZone
            onDrop={handleDropZoneDrop}
            accept="image/*"
            type="image"
            allowMultiple
            disabled={isUploading || Boolean(deletingPhoto)}
          >
            {uploadedFilesList}
            {fileUpload}
          </DropZone>

          {/* Processing Progress */}
          {isUploading && processingProgress > 0 && (
            <BlockStack gap="200">
              <Text variant="bodyMd">
                {processingProgress < 50 ? 'Processing images...' : processingProgress < 95 ? 'Uploading images...' : 'Updating order...'}
              </Text>
              <ProgressBar progress={processingProgress} />
            </BlockStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default AddKidPhotosModal;
